import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Bookmark, BookmarkCheck, Clock } from "lucide-react";
import { toast } from "sonner";

const renderBody = (body) => {
  const blocks = body.split("\n\n");
  return blocks.map((b, i) => {
    const parts = b.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i}>
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={j}>{p.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{p}</span>
          )
        )}
      </p>
    );
  });
};

const Article = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    api.get(`/articles/${id}`).then((r) => setArticle(r.data));
    api.get("/favorites").then((r) => {
      setFavIds(new Set(r.data.favorites.map((f) => f.id)));
    }).catch(() => {});
  }, [id]);

  const toggleFav = async () => {
    if (favIds.has(id)) {
      await api.delete(`/favorites/${id}`);
      const s = new Set(favIds); s.delete(id); setFavIds(s);
      toast("Removed from saved");
    } else {
      await api.post("/favorites", { article_id: id });
      const s = new Set(favIds); s.add(id); setFavIds(s);
      toast.success("Saved to your library");
    }
  };

  if (!article) return <div className="p-10 text-neutral-500" data-testid="article-loading">Loading…</div>;

  const saved = favIds.has(id);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/library" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6" data-testid="back-to-library">
        <ArrowLeft className="w-4 h-4" /> Back to library
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 text-sm">
          <span className="overline text-amber-500">{article.category}</span>
          <span className="text-neutral-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_time} min read</span>
        </div>
        <button
          onClick={toggleFav}
          data-testid="favorite-btn"
          className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border ${
            saved ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
          }`}
        >
          {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          {saved ? "Saved" : "Save"}
        </button>
      </div>

      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-6" data-testid="article-title">{article.title}</h1>
      <p className="text-lg text-neutral-400 mb-10 leading-relaxed italic">{article.excerpt}</p>

      <article className="prose-suitor" data-testid="article-body">{renderBody(article.body)}</article>

      <div className="mt-16 border-t border-white/5 pt-8">
        <Link to="/coach" className="btn-amber inline-flex items-center gap-2" data-testid="cta-ask-coach">
          Ask the coach about this
        </Link>
      </div>
    </div>
  );
};

export default Article;
